import { defineLocalStore } from '@/utils/localStore'
import { ref } from 'vue'
import type { DepartmentListItem } from '@/utils/types/departments'
import { confirm, handleAsync, setSnackbar } from 'best-modules/plugins'
import axios from '@/plugins/axios'
import { urlDepartmentDelete, urlDepartmentList } from '@/utils/urls'
import { openDialog } from '@/dialog'
import {
  pushDirectoryItem,
  updateDirectoryItem,
  removeDirectoryItem,
} from '@/directory'
import { omit, pick } from 'lodash'

const findDepartment = (
  departmentId: number,
  arr: DepartmentListItem[]
): DepartmentListItem | null => {
  if (!departmentId) return null
  for (const idx in arr) {
    const item = arr[idx]
    if (item.id === departmentId) {
      return item
    } else if (item.allChildren?.length) {
      const dep = findDepartment(departmentId, item.allChildren)
      if (dep) return dep
    }
  }
}

export const {
  useLocalStore: useDepartmentList,
  initLocalStore: initDepartmentList,
} = defineLocalStore('departmentList', () => {
  const departmentList = ref<DepartmentListItem[]>([])

  const pushDepartment = (department: DepartmentListItem, parentId: number) => {
    const dep = {
      employees: [],
      allChildren: [],
      supervisor: null,
      ...department,
    }
    const parentDepartment = findDepartment(parentId, departmentList.value)

    if (parentDepartment) {
      parentDepartment.allChildren.push(dep)
    } else {
      departmentList.value.push(dep)
    }
  }

  const removeDepartment = (departmentId: number, parentId: number) => {
    const parentDepartment = findDepartment(parentId, departmentList.value)
    if (parentDepartment) {
      parentDepartment.allChildren = parentDepartment.allChildren.filter(d => {
        return d.id !== departmentId
      })
    } else {
      departmentList.value = departmentList.value.filter(
        d => d.id !== departmentId
      )
    }
  }

  const moveDepartment = (
    department: DepartmentListItem,
    from: number,
    to: number
  ) => {
    removeDepartment(department.id, from)
    pushDepartment(department, to)
  }

  const getDepartmentList = () => {
    return handleAsync('getDepartmentList', () => {
      return axios.get(urlDepartmentList()).then(res => {
        departmentList.value = res.data
      })
    })
  }

  const createDepartment = (parentId: number) => {
    openDialog({
      name: 'Department',
      action: 'create',
      dialogData: {
        department: {
          parentId,
        },
      },
      params: {
        cb: dep => {
          pushDepartment({ ...dep, children: [], employees: [] }, parentId)
          pushDirectoryItem('departmentList', dep)
        },
      },
    })
  }

  const updateDepartment = (departmentId: number) => {
    const department: DepartmentListItem = findDepartment(
      departmentId,
      departmentList.value
    )

    if (!department) return

    const updateItem = (updatedDepartment: DepartmentListItem) => {
      const dep = findDepartment(department.id, departmentList.value)
      Object.assign(dep, updatedDepartment)
      updateDirectoryItem(
        'departmentList',
        pick(updatedDepartment, ['id', 'name', 'parentId', 'isActive']),
        // @ts-expect-error TODO types in best-modules
        updatedDepartment.id
      )
    }

    openDialog({
      name: 'Department',
      action: 'update',
      dialogData: {
        department: omit(department, 'children'),
        updateItem: updateItem,
      },
      params: {
        cb: async updatedDepartment => {
          if (updatedDepartment.parentId !== department.parentId) {
            moveDepartment(
              department,
              department.parentId,
              updatedDepartment.parentId
            )
          }
          updateItem(updatedDepartment)
        },
      },
    })
  }

  const deleteDepartment = (departmentId: number) => {
    const department: DepartmentListItem = findDepartment(
      departmentId,
      departmentList.value
    )

    if (!department) return

    return confirm({
      text: 'Ви впевненні що хочете видалити департамент назавжди?',
    }).then(async () => {
      const handleChildren = async (dep: DepartmentListItem) => {
        moveDepartment(dep, dep.parentId, 0)
        Object.assign(dep, { parentId: 0 })
        if (Array.isArray(dep.allChildren)) {
          for (const child of dep.allChildren) {
            await handleChildren(child)
          }
        }
      }

      return axios.delete(urlDepartmentDelete(department.id)).then(async () => {
        if (Array.isArray(department.allChildren)) {
          for (const child of department.allChildren) {
            await handleChildren(child)
          }
        }

        removeDepartment(department.id, department.parentId)
        removeDirectoryItem('departmentList', department.id)

        setSnackbar({ text: 'Департамент успішно видалено' })
      })
    })
  }

  getDepartmentList()

  return {
    departmentList,
    createDepartment,
    updateDepartment,
    deleteDepartment,
  }
})
